import { useState, useEffect, useCallback } from "react";
import { Save, Plus, Building2, GitBranch, MessageSquare, Sliders, Users as UsersIcon, UserCircle2, History, Crown, Database } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { PageHeader, Loading, SectionCard, Pill, Stamp } from "../components/kit";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Field, Input, Select, Textarea } from "../components/form";
import { FormModal } from "../components/form";
import { refreshVendorTaxonomy } from "../components/VendorProfile";
import { useAuth } from "../context/AuthContext";
import { titleCase, isAdminRole } from "../lib/constants";
import MyAccount from "../components/MyAccount";
import TeamManager from "../components/TeamManager";
import AuditLog from "../components/AuditLog";
import PropertyDataCenter from "../components/PropertyDataCenter";
import { toast } from "sonner";

export default function Settings() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const isAdmin = isAdminRole(user?.role);

  const load = useCallback(async () => { const { data } = await api.get("/settings"); setS(data); }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    try { const { data } = await api.put("/settings", { ...s, ...patch }); setS(data); toast.success("Settings saved"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!s) return <Loading />;
  const setBiz = (k, v) => setS({ ...s, business: { ...s.business, [k]: v } });
  const setTw = (k, v) => setS({ ...s, twilio: { ...(s.twilio || {}), [k]: v } });
  const setEm = (k, v) => setS({ ...s, email: { ...(s.email || {}), [k]: v } });

  return (
    <div className="animate-fade-up">
      <PageHeader title="Settings" subtitle="Configure branding, pipeline, integrations, and your team." testid="settings-header" />
      <Tabs defaultValue="account">
        <TabsList className="bg-white border border-slate-200 flex-wrap h-auto">
          {[["account", "My Account", UserCircle2], ["business", "Business", Building2], ["pipeline", "Pipeline & Types", GitBranch], ["scoring", "Lead Scoring", Sliders], ["integrations", "Integrations", MessageSquare], ["mls", "MLS & Property Data", Database], ["team", "Team", UsersIcon], ["audit", "Audit Log", History]].map(([v, l, I]) => (
            <TabsTrigger key={v} value={v} data-testid={`settings-tab-${v}`} className="data-[state=active]:bg-navy-800 data-[state=active]:text-white"><I className="w-3.5 h-3.5 mr-1.5" />{l}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="account" className="mt-5"><MyAccount /></TabsContent>
        <TabsContent value="audit" className="mt-5"><AuditLog /></TabsContent>
        <TabsContent value="mls" className="mt-5"><PropertyDataCenter /></TabsContent>

        <TabsContent value="business" className="mt-5">
          <SectionCard title="Business Information" action={<Stamp prefix="Last saved by" by={s.updated_by_name} at={s.updated_at} testid="settings-updated-stamp" />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Business name"><Input value={s.business?.name || ""} onChange={(e) => setBiz("name", e.target.value)} data-testid="biz-name" /></Field>
              <Field label="Email"><Input value={s.business?.email || ""} onChange={(e) => setBiz("email", e.target.value)} /></Field>
              <Field label="Phone"><Input value={s.business?.phone || ""} onChange={(e) => setBiz("phone", e.target.value)} /></Field>
              <Field label="Address"><Input value={s.business?.address || ""} onChange={(e) => setBiz("address", e.target.value)} /></Field>
              <Field label="Brand navy"><Input type="color" value={s.business?.brand_navy || "#0F172A"} onChange={(e) => setBiz("brand_navy", e.target.value)} className="h-10" /></Field>
              <Field label="Brand gold"><Input type="color" value={s.business?.brand_gold || "#D4AF37"} onChange={(e) => setBiz("brand_gold", e.target.value)} className="h-10" /></Field>
            </div>
            <Button className="bg-navy-800 text-white mt-4" onClick={() => save({})} data-testid="save-business"><Save className="w-4 h-4 mr-1.5" />Save</Button>
          </SectionCard>
        </TabsContent>

        <TabsContent value="pipeline" className="mt-5 space-y-4">
          <ListEditor title="Pipeline Stages" items={s.pipeline_stages} onSave={(v) => save({ pipeline_stages: v })} testid="pipeline-stages" />
          <ListEditor title="Contact Types" items={s.contact_types} onSave={(v) => save({ contact_types: v })} testid="contact-types" />
          <ListEditor title="Vendor / Professional Categories" items={s.vendor_categories} onSave={(v) => save({ vendor_categories: v })} testid="vendor-categories" raw />
          <ListEditor title="Trades & Specialties (multi-select on professionals)" items={s.vendor_specialties} onSave={(v) => save({ vendor_specialties: v })} testid="vendor-specialties" raw />
          <ListEditor title="Property Types" items={s.property_types} onSave={(v) => save({ property_types: v })} testid="property-types" />
          <ListEditor title="Lead Sources" items={s.lead_sources} onSave={(v) => save({ lead_sources: v })} testid="lead-sources" />
          <ListEditor title="Tags" items={s.tags} onSave={(v) => save({ tags: v })} testid="tags" />
        </TabsContent>

        <TabsContent value="scoring" className="mt-5">
          <SectionCard title="Lead Scoring Rules">
            <p className="text-sm text-slate-500 mb-4">Configure how each factor contributes to a lead's score (0–100). Nothing is hard-coded.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {Object.entries(s.lead_scoring || {}).map(([k, v]) => (
                <Field key={k} label={titleCase(k)}><Input type="number" value={v} onChange={(e) => setS({ ...s, lead_scoring: { ...s.lead_scoring, [k]: Number(e.target.value) } })} data-testid={`score-${k}`} /></Field>
              ))}
            </div>
            <Button className="bg-navy-800 text-white mt-4" onClick={() => save({})} data-testid="save-scoring"><Save className="w-4 h-4 mr-1.5" />Save</Button>
          </SectionCard>
        </TabsContent>

        <TabsContent value="integrations" className="mt-5 space-y-4">
          <SectionCard title="Twilio SMS">
            <p className="text-sm text-slate-500 mb-4">Add your Twilio credentials to send real SMS. Until then, messages are recorded to the timeline.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Account SID"><Input value={s.twilio?.account_sid || ""} onChange={(e) => setTw("account_sid", e.target.value)} data-testid="twilio-sid" /></Field>
              <Field label="Auth Token"><Input type="password" value={s.twilio?.auth_token || ""} onChange={(e) => setTw("auth_token", e.target.value)} data-testid="twilio-token" /></Field>
              <Field label="From Number"><Input value={s.twilio?.from_number || ""} onChange={(e) => setTw("from_number", e.target.value)} placeholder="+1208..." data-testid="twilio-from" /></Field>
            </div>
            {isAdmin ? <Button className="bg-navy-800 text-white mt-4" onClick={() => save({})} data-testid="save-twilio"><Save className="w-4 h-4 mr-1.5" />Save</Button> : <p className="text-xs text-amber-600 mt-3">Admin access required to edit.</p>}
          </SectionCard>
          <SectionCard title="Email (SMTP)">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="SMTP Host"><Input value={s.email?.smtp_host || ""} onChange={(e) => setEm("smtp_host", e.target.value)} placeholder="smtp.gmail.com" data-testid="smtp-host" /></Field>
              <Field label="SMTP Port"><Input type="number" value={s.email?.smtp_port || 587} onChange={(e) => setEm("smtp_port", Number(e.target.value))} /></Field>
              <Field label="SMTP User"><Input value={s.email?.smtp_user || ""} onChange={(e) => setEm("smtp_user", e.target.value)} data-testid="smtp-user" /></Field>
              <Field label="SMTP Password"><Input type="password" value={s.email?.smtp_password || ""} onChange={(e) => setEm("smtp_password", e.target.value)} data-testid="smtp-pass" /></Field>
              <Field label="From Email"><Input value={s.email?.from_email || ""} onChange={(e) => setEm("from_email", e.target.value)} /></Field>
              <Field label="Signature"><Input value={s.email?.signature || ""} onChange={(e) => setEm("signature", e.target.value)} /></Field>
            </div>
            {isAdmin ? <Button className="bg-navy-800 text-white mt-4" onClick={() => save({})} data-testid="save-email"><Save className="w-4 h-4 mr-1.5" />Save</Button> : <p className="text-xs text-amber-600 mt-3">Admin access required to edit.</p>}
          </SectionCard>
        </TabsContent>

        <TabsContent value="team" className="mt-5"><TeamManager isAdmin={isAdmin} me={user} /></TabsContent>
      </Tabs>
    </div>
  );
}

function ListEditor({ title, items, onSave, testid, raw }) {
  const [val, setVal] = useState((items || []).join(", "));
  useEffect(() => { setVal((items || []).join(", ")); }, [items]);
  return (
    <SectionCard title={title} testid={testid}>
      <div className="flex flex-wrap gap-2 mb-3 max-h-40 overflow-y-auto">{(items || []).map((i) => <Pill key={i}>{raw ? i : titleCase(i)}</Pill>)}</div>
      {raw ? <Textarea value={val} onChange={(e) => setVal(e.target.value)} placeholder="Comma separated values" /> : <Input value={val} onChange={(e) => setVal(e.target.value)} placeholder="Comma separated values" />}
      <Button variant="outline" className="mt-3" onClick={() => { onSave(val.split(",").map((x) => x.trim()).filter(Boolean)); refreshVendorTaxonomy(); }} data-testid={`save-${testid}`}><Save className="w-4 h-4 mr-1.5" />Save</Button>
    </SectionCard>
  );
}
